import { useStore } from '@/hooks/useStore';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactNode, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';

import PaymentSuccessImg from '@/assets/images/payment-success.svg';
import PaymentFailImg from '@/assets/images/payment-fail.svg';
import { ButtonCus } from '@/components/common/Button';
import { isPathEk } from '@/utils/routes';
import { ekDomain } from '@/configs/constants';
import { formatCurrency } from '@/utils/formatNumber';
import { PAYMENT_STATUS } from '@/types/enums';
import { URL_CONFIG } from '@/configs/environment';

export default observer(function StatusPaymentPage() {
    // hooks
    const { t } = useTranslation();
    const { paymentId } = useParams();
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    // store
    const {
        bookingStore: { getStatusPayment, paymentStatusHistory, setObservable }
    } = useStore();

    // state
    // const [payment, setPayment] = useState<any>(undefined);

    // lifeCycle
    useEffect(() => {
        paymentId && onFetch(paymentId);

        return () => {
            setObservable('paymentStatusHistory', {});
        };
    }, [paymentId]);

    const onFetch = async (paymentId: number | string) => {
        if (paymentId) {
            const res = await flowResult(getStatusPayment(paymentId));
            if (res) {
                // setPayment({ ...res.data });
            } else {
                // setPayment(null);
            }
        }
    };

    const getLinkReservationHistory = (orderId) => {
        const orderIdPath = orderId ? `/${orderId}` : '';
        return isEk ?
            `/${ekDomain}/my-account/rental-history${orderIdPath}` :
            `/my-account/rental-history${orderIdPath}`;
    };

    const getTitlePaymentStatus = useMemo((): string | ReactNode => {
        if (paymentStatusHistory?.paymentStatus === PAYMENT_STATUS.PAID_SUCCESS) {
            return t('words_title.payment_successful');
        } else if (paymentStatusHistory?.paymentStatus === PAYMENT_STATUS.PAID_FAIL) {
            return <Trans i18nKey='booking_bike.sentences.payment_failed' />;
        }
        return '';
    }, [paymentStatusHistory?.paymentStatus]);

    return (
        <div className='relative py-[min(80px,12vw)]'>
            <div className='flex px-4 text-center justify-center md:mb-10 mb-6 title-page-2'>
                {/* {
                    paymentStatusHistory?.paymentStatus === PAYMENT_STATUS.PAID_SUCCESS ?
                        t('words_title.payment_successful') :
                        paymentStatusHistory?.paymentStatus === PAYMENT_STATUS.PAID_FAIL ? t('booking_bike.sentences.payment_failed') : ''
                } */}
                {getTitlePaymentStatus}
            </div>

            <div className='flex px-4 flex-col items-center sm:mb-0 mb-4'>
                {paymentStatusHistory?.paymentStatus && (
                    <div className='flex justify-center mb-8 max-h-[35vh] max-w-full'>
                        <img
                            src={
                                paymentStatusHistory?.paymentStatus === PAYMENT_STATUS.PAID_SUCCESS ?
                                    PaymentSuccessImg :
                                    paymentStatusHistory?.paymentStatus === PAYMENT_STATUS.PAID_FAIL ? PaymentFailImg : ''
                            }
                            alt=''
                        />
                    </div>
                )}
                {/* <div className='flex justify-center text-fs-mid20 mb-4 title-page-2'>
                    <span>
                        {payment && t(`${PAYMENT_TYPE_OBJECT[payment?.paymentType].label}`)}
                    </span>
                    <span>{payment?.paymentType === PAYMENT_TYPE.OTHER && `: ${t(`${ADDITIONAL_REQUEST_PAYMENT_TYPE[payment?.additionalType]?.key}`)}`}</span>
                </div> */}

                <div className='text-fs-mid16 box-item mb-8'>
                    <div className='flex flex-col gap-4'>
                        {
                            paymentStatusHistory?.paymentStatus === PAYMENT_STATUS.PAID_SUCCESS && (
                                <div className='flex gap-2 justify-between'>
                                    <div className='w-fit'>
                                        {t('booking_bike.field.reservation_id')}
                                        :
                                    </div>
                                    <div className='font-semibold'>{paymentStatusHistory?.reservationCode || '--'}</div>
                                </div>
                            )
                        }
                        <div className='flex gap-2 justify-between'>
                            <div className='w-fit'>
                                {t('booking_bike.field.payment_method')}
                                :
                            </div>
                            <div className='font-semibold'>{paymentStatusHistory?.paymentMethod || '--'}</div>
                        </div>
                        <div className='flex gap-2 justify-between'>
                            <div className='w-fit'>
                                {t('booking_bike.field.total_payment_2')}
                                :
                            </div>
                            <div className='font-semibold'>
                                {
                                    formatCurrency(paymentStatusHistory?.totalPayment) ?
                                        `${t('currency.currency_symbol')}${formatCurrency(paymentStatusHistory?.totalPayment)}` :
                                        '--'
                                }
                            </div>
                        </div>
                    </div>
                    {paymentStatusHistory?.paymentStatus === PAYMENT_STATUS.PAID_SUCCESS && (
                        <div className='mt-4 sm:text-left text-center'>
                            お支払いの詳細は、
                            <br className='sm:hidden block' />
                            予約履歴画面にてご確認が出来ます。
                        </div>
                    )}
                </div>

            </div>

            {
                paymentStatusHistory && (
                    <div className='flex justify-center xsm:gap-4 gap-2 p-4 sm:bg-none sm:shadow-none sm:static bg-background-3 shadow-shadow-3 bottom-0 sticky'>
                        {
                            (paymentStatusHistory?.paymentStatus === PAYMENT_STATUS.PAID_SUCCESS) && (
                                <Link to={getLinkReservationHistory(paymentStatusHistory?.orderId)}>
                                    <ButtonCus
                                        variant='default'
                                        size='md'
                                        className='sm:w-[288px] w-full'
                                    >
                                        {t('button.reservation_history')}
                                    </ButtonCus>
                                </Link>
                            )
                        }
                        <Link to={isEk ? `${URL_CONFIG.APP.url}/${ekDomain}` : `${URL_CONFIG.DOMAIN_WORDPRESS.url}`}>
                            <ButtonCus
                                variant='outline'
                                size='md'
                                className='sm:w-[288px] w-full'
                            >
                                {t('button.go_to_home')}
                            </ButtonCus>
                        </Link>

                    </div>
                )
            }

        </div>
    );
});
